import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link as RouterLink} from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip,
  CircularProgress, Divider, Link, Paper,
  Table, TableBody, TableCell,
  TableHead, TableRow, Typography
} from "@mui/material";
import PaidIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BienInfoSection from "./BienInfoSection";
import RevisionLoyerModal from "./RevisionLoyerModal";
import { fetchWithAuth } from "utils/fetchWithAuth";
import { formatDate, formatMonth } from "utils/dateFormat";

const getLastLocation = (locations) => {
  const enCours = locations?.find(loc => !loc.date_sortie);
  if (enCours) return enCours;
  const terminees = locations?.filter(loc => loc.date_sortie).sort((a, b) =>
    new Date(b.date_sortie) - new Date(a.date_sortie)
  );
  return terminees?.[0] || null;
};

const formatPct = v => (v > 0 ? "+" : "") + v.toFixed(1) + "%";

export default function FicheLot({ lotId }) {
  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [revisionData, setRevisionData] = useState(null);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedMonth = searchParams.get("mois") || new Date().toISOString().slice(0, 7);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const API_URL = process.env.REACT_APP_API_URL;
    const query = selectedMonth ? `?mois=${selectedMonth}` : '';
    fetchWithAuth(`${API_URL}/api/lots/${lotId}/${query}`)
      .then(res => {
        if (!res.ok) throw new Error("Erreur lors du chargement du lot");
        return res.json();
      })
      .then(data => {
        setLot(data);
        setLoading(false);
      })
      .catch(e => {
        setError("Impossible de charger le lot");
        setLoading(false);
      });

    fetchWithAuth(`${API_URL}/api/lots/${lotId}/locations/`)
      .then(res => res.json())
      .then(setLocations);

  }, [lotId, selectedMonth]);

  const lastLoc = getLastLocation(locations);
  
  const fetchRevision = useCallback(async() => {
    if (!lastLoc?.id) return;

    const API_URL = process.env.REACT_APP_API_URL;

    fetchWithAuth(`${API_URL}/api/locations/${lastLoc.id}/revision/`)
      .then(res => {
        if (!res.ok) throw new Error("Erreur récupération révision");
        return res.json();
      })
      .then(data => {
        setRevisionData(data);
      })
      .catch(err => {
        console.error("Erreur révision :", err);
        setRevisionData(null);
      });
  }, [lastLoc?.id]);

  useEffect(() => {
    fetchRevision()
  }, [fetchRevision]);

  if (loading)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!lot) return null;

  const bien = lot.bien || {};
  const locationsDuMois = lot.locations_du_mois || [];
  const current = revisionData?.current;
  const revision = revisionData?.revision;
  const theorique = revisionData?.theorique;
  const meta = revisionData?.meta;
  const canGenerate = ["envoyable", "en_retard"].includes(revision?.statut) && !meta?.already_notified
  const isInitial = current?.date_reference === current?.date_entree;
  const labelCurrentIRL = isInitial
  ? "IRL signature"
  : "IRL appliqué";
  const isFutureRevision = 
    revision?.statut === "irl_indisponible" ||
    revision?.raison?.includes("bail_moins_un_an")
  const monthsLate = (() => {
    if (revision?.statut !== "en_retard") return 0

    const end = new Date(revision.date_effet_theorique)
    const effect = new Date(revision.date_effet)

    return (
      (effect.getFullYear() - end.getFullYear()) * 12 +
      (effect.getMonth() - end.getMonth())
    )
  })()

  const handleOpenRevisionModal = () => {
    setRevisionModalOpen(true);
  };

  return (
    <>
      <Card sx={{ maxWidth: 1000, mx: "auto", mt: 4, mb: 4, p: 0 }}>
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            {bien.est_residence && bien.nom_residence
              ? `${bien.nom_residence} - ${lot.nom}`
              : lot.nom
            }
          </Typography>
          <Typography color="text.secondary">{formatMonth(selectedMonth)}</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Informations sur le bien
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, background: "#f7fafb" }}>
            <BienInfoSection bien={bien} />
          </Paper>
          {lastLoc && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              background: "#EEF8FF",
              borderRadius: 2,
              width: "100%",
              boxSizing: 'border-box',
            }}
          >
            <Typography fontWeight="bold" sx={{ color: "#2196F3", mb: 1 }}>
              Révision du loyer
            </Typography>
            <Box
              sx={{
                maxWidth: 1000,
                mx: "auto",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 4,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" sx={{ color: "#607D8B", mb: 1 }}>
                  Loyer actuel
                </Typography>

                <Typography>
                  Loyer : <b>{current?.loyer ?? lastLoc.loyer_nu} €</b>
                </Typography>

                <Typography sx={{ fontSize: "0.9em" }}>
                  Depuis le :{" "}
                  <b>
                    {current?.date_reference
                      ? new Date(current.date_reference).toLocaleDateString()
                      : "-"}
                  </b>
                </Typography>

                {current?.trimestre && (
                  <Typography sx={{ fontSize: "0.9em" }}>
                    {labelCurrentIRL} ({current.trimestre}) : {current.irl}
                  </Typography>
                )}

                <Typography sx={{ fontSize: "0.9em" }}>
                  Signature bail : {new Date(current?.date_signature|| lastLoc.date_signature).toLocaleDateString()}
                </Typography>

                <Typography sx={{ fontSize: "0.9em" }}>
                  Entrée : {new Date(current?.date_entree || lastLoc.date_entree).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" sx={{ color: "#4CAF50", mb: 1 }}>
                  Révision légale
                </Typography>

                <Typography>
                  Loyer :
                  <b>
                    {revision?.loyer_revise
                      ? ` ${revision.loyer_revise} €`
                      : " Non révisable"}
                  </b>
                </Typography>

                {revision?.date_effet && !isFutureRevision && (
                  <Typography sx={{ fontSize: "0.9em" }}>
                    A compter du : <b>{new Date(revision.date_effet).toLocaleDateString()}</b>
                  </Typography>
                )}

                {revision?.date_effet && revision?.date_revision && isFutureRevision && (
                  <>
                    <Typography sx={{ fontSize: "0.9em" }}>
                      Prochaine révision : <b>{new Date(revision.date_revision).toLocaleDateString()}</b>
                    </Typography>
                    <Typography sx={{ fontSize: "0.9em" }}>
                      Pour un effet au : <b>{new Date(revision.date_effet).toLocaleDateString()}</b>
                    </Typography>
                  </>
                )}

                {revision?.trimestre_base && revision?.irl_base && 
                  revision?.trimestre_cible && revision?.irl_cible && (
                  <>
                    <Typography sx={{ fontSize: "0.9em" }}>
                      IRL référence ({revision.trimestre_base}) : {revision.irl_base}
                    </Typography>
                    <Typography sx={{ fontSize: "0.9em" }}>
                      IRL appliqué ({revision.trimestre_cible}) : {revision.irl_cible}
                    </Typography>
                  </>
                )}

                {revision?.augmentation && (
                  <Typography>
                    Augmentation : <b>{revision.augmentation.euros} €</b>{" "}
                    (<span style={{ fontSize: "0.9em", color: "#666" }}>
                      {formatPct(revision.augmentation.pourcentage)}
                    </span>)
                  </Typography>
                )}

                <Typography sx={{ fontSize: "0.85em", mt: 1 }}>
                  {revision?.statut === "trop_tot" && (
                    <span style={{ color: "#9E9E9E" }}>
                      Le bail est trop récent pour être révisé
                    </span>
                  )}

                  {revision?.statut === "envoyable" && !meta?.already_notified && (
                    <span style={{ color: "#4CAF50" }}>
                      Révision applicable ✅
                    </span>
                  )}

                  {revision?.statut === "envoyable" && meta?.already_notified && (
                    <span style={{ color: "#4CAF50" }}>
                      La révision a été appliquée<br/>
                      Déjà notifiée le{" "}
                      <b>{new Date(meta.date_notification).toLocaleDateString()}</b>
                    </span>
                  )}

                  {revision?.statut === "en_retard" && !meta?.already_notified && (
                    <span style={{ color: "#ff9800" }}>
                      Révision appliquée avec <b>{monthsLate} mois de retard</b>
                    </span>
                  )}

                  {revision?.statut === "en_retard" && meta?.already_notified && (
                    <span style={{ color: "#ff9800" }}>
                      La révision a été appliquée en retard<br/>
                      Déjà notifiée le{" "}
                      <b>{new Date(meta.date_notification).toLocaleDateString()}</b>
                    </span>
                  )}

                  {revision?.statut === "irl_indisponible" && (
                    <span style={{ color: "#9E9E9E" }}>
                      IRL non publié
                    </span>
                  )}

                  {revision?.statut === "non_avantageuse" && (
                    <span style={{ color: "#9E9E9E" }}>
                      IRL en baisse (révision non applicable)
                    </span>
                  )}

                  {revision?.statut === "terminee" && (
                    <span style={{ color: "#9E9E9E" }}>
                      Bail terminé
                    </span>
                  )}
                </Typography>

                {revision?.date_limite 
                  && !isFutureRevision
                  && revision?.statut !== "en_retard"
                  && !meta?.already_notified && (
                  <Typography sx={{ fontSize: "0.8em" }}>
                    Limite :{" "}
                    <b>{new Date(revision.date_limite).toLocaleDateString()}</b>
                  </Typography>
                )}

                {revision?.date_debut_envoi && revision?.date_limite && isFutureRevision && (
                  <Typography sx={{ fontSize: "0.75em", color: "#777" }}>
                    Notification : du{" "}
                    <b>{new Date(revision.date_debut_envoi).toLocaleDateString()}</b> au{" "}
                    <b>{new Date(revision.date_limite).toLocaleDateString()}</b>

                  </Typography>
                )}

                <Button
                  variant="contained"
                  disabled={!canGenerate}
                  onClick={handleOpenRevisionModal}
                  sx={{ mt: 2 }}
                >
                  Réviser le loyer
                </Button>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" sx={{ color: "#FF9800", mb: 1 }}>
                  Loyer Théorique
                </Typography>

                <Typography>
                  {theorique === null ? (
                    "Bail terminé"
                  ) : (
                    <>
                      Loyer :{" "}
                      <b>
                        {theorique?.loyer ?? "-"} €
                      </b>
                    </>
                  )}
                </Typography>

                {theorique?.trimestre && (
                  <Typography sx={{ fontSize: "0.9em" }}>
                    IRL ({theorique.trimestre}) : {theorique.irl}
                  </Typography>
                )}

                {theorique?.augmentation && (
                  <>
                    <Typography>
                      Augmentation : <b>{theorique.augmentation.euros}€</b>{" "}
                      (<span style={{ fontSize: "0.9em", color: "#666" }}>
                        {formatPct(theorique.augmentation.pourcentage)}
                      </span>)
                    </Typography>

                    <Typography sx={{ fontSize: "0.8em", color: "text.secondary", mt: 1 }}>
                      Si toutes les révisions avaient été appliquées
                    </Typography>
                  </>
                )}
              </Box>

            </Box>
          </Paper>
          )}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 3 }}>
            Locations du mois
          </Typography>
          {locationsDuMois.length  > 0 ? (
              <Box sx={{ py: 1, px: 2, border: "1px solid #eee", borderRadius: 2, mb: 2, overflowX: 'auto' }}>
                  <Table size="small" sx={{ mt: 1, minWidth: 600 }}>
                  <TableHead>
                      <TableRow sx={{ backgroundColor: "#f7fafb" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Locataire</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Entrée</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Sortie</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Loyer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Montant dû</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Reçu</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {locationsDuMois.map(loc => (
                      <TableRow key={loc.id}>
                          <TableCell>
                            {loc.locataires && loc.locataires.length > 0 ? (
                              loc.locataires
                                .slice()
                                .sort((a, b) => {
                                  if (loc.locataire_referent && a.id === loc.locataire_referent.id) return -1;
                                  if (loc.locataire_referentreferent && b.id === loc.locataire_referentreferent.id) return 1;
                                  return 0;
                                })
                                .map((l) => (
                                  <div key={l.id}>
                                    <Link
                                      component={RouterLink}
                                      to={`/locataires/${l.id}`}
                                      sx={{ textDecoration: "none", color: "#1565c0", fontWeight: 500 }}
                                    >
                                      {l.prenom} {l.nom}
                                    </Link>
                                  </div>
                                ))
                            ) : (
                              <Typography color="text.secondary">Vacant</Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(loc.date_entree)}</TableCell>
                          <TableCell>
                          {loc.date_sortie ? formatDate(loc.date_sortie) : "en cours"}
                          </TableCell>
                          <TableCell>{loc.loyer} €</TableCell>
                          <TableCell>{loc.montant_du} €</TableCell>
                          <TableCell>{loc.montant_recu} €</TableCell>
                          <TableCell>
                            <Chip
                              label={loc.statut_paiement || ""}
                              color={
                                loc.statut_paiement === "acquitté"
                                  ? "success"
                                : loc.statut_paiement === "en retard"
                                  ? "error"
                                : loc.statut_paiement === "dû|partiel"
                                  ? "warning"
                                : "default"
                              }
                              size="small"
                              icon={
                                loc.paye ? (
                                  <PaidIcon fontSize="small" sx={{ color: "inherit", mr: 0.2, ml: 0.5 }}/>
                                ) : (
                                  <ErrorIcon fontSize="small" sx={{ color: "inherit", mr: 0.2, ml: 0.5 }}/>
                                )  
                              }
                            />
                          </TableCell>
                      </TableRow>
                      ))}
                  </TableBody>
                  </Table>
              </Box>
              ) : (
              <Alert severity="info" sx={{ my: 2 }}>Aucune occupation ce mois-ci</Alert>
            )}
        </CardContent>
      </Card>
      
      <RevisionLoyerModal
        open={revisionModalOpen}
        onClose={() => setRevisionModalOpen(false)}
        location={lastLoc}
        lot={lot}
        revision={revisionData?.revision}
        onValidated={fetchRevision}
      />
    </>
  );
}